import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

// Use environment variable or fallback to production URL
const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'https://devisfast-backend.onrender.com/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  // Sprint 2 task 5 (04/05/2026) : subscription status partagé globalement
  const [subscription, setSubscription] = useState(null);

  // Helper : fetch le statut d'abonnement et le stocke dans le contexte
  const refreshSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      return null;
    }
  }, []);

  // Configure axios defaults and interceptors
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Sprint 2 task 5 : intercepteur global pour gérer les 403 backend
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;

        // Cas 1 : subscription_required (vieux flow legacy + canceled)
        // -> redirection automatique vers /subscription
        if (status === 403 && detail === 'subscription_required') {
          window.location.href = '/subscription';
          return Promise.reject(error);
        }

        // Cas 2 : readonly_mode (user expired qui tente d'écrire)
        // -> toast avec CTA + rafraîchir le subscription pour mettre à jour les bandeaux
        if (status === 403 && detail === 'readonly_mode') {
          // Force re-fetch du status (le backend a peut-être basculé en expired)
          refreshSubscription();
          // Toast clair avec action
          toast.error('Activez votre abonnement pour continuer', {
            description: 'Cette action nécessite un abonnement actif.',
            action: {
              label: 'Activer',
              onClick: () => { window.location.href = '/subscription'; }
            },
            duration: 6000
          });
          return Promise.reject(error);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token, refreshSubscription]);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        // Also fetch company
        try {
          const companyRes = await axios.get(`${API}/company`);
          setCompany(companyRes.data);
        } catch (e) {
          // No company yet, that's ok
        }
        // Sprint 2 task 5 : fetch subscription au mount pour avoir le bon bandeau dès l'arrivée
        await refreshSubscription();
      } catch (error) {
        // Token invalid, clear it
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, refreshSubscription]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    // Store token
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    // Update state
    setToken(access_token);
    setUser(userData);

    // Fetch company
    try {
      const companyRes = await axios.get(`${API}/company`);
      setCompany(companyRes.data);
    } catch (e) {
      // No company yet
    }

    // Sprint 2 task 5 : fetch subscription juste après le login
    await refreshSubscription();

    return userData;
  }, [refreshSubscription]);

  const signup = useCallback(async (email, password, fullName) => {
    const response = await axios.post(`${API}/auth/signup`, {
      email,
      password,
      full_name: fullName
    });
    const { access_token, user: userData } = response.data;

    // Store token
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    // Update state
    setToken(access_token);
    setUser(userData);

    // Sprint 2 task 5 : fetch subscription après le signup (user toujours en trialing)
    await refreshSubscription();

    return userData;
  }, [refreshSubscription]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setCompany(null);
    setSubscription(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const updateCompany = useCallback((newCompany) => {
    setCompany(newCompany);
  }, []);

  // Sprint 2 task 5 : helper exposé aux pages pour rediriger un click "écriture" vers /subscription
  // Retourne true si l'action peut continuer (trialing/active), false si elle doit être bloquée (expired/canceled)
  const canWrite = useCallback(() => {
    if (!subscription) return true; // Optimiste : si pas encore chargé, on laisse passer (le backend tranchera)
    const status = subscription.subscription_status;
    return status === 'trialing' || status === 'active';
  }, [subscription]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      company,
      loading,
      login,
      signup,
      logout,
      updateCompany,
      // Sprint 2 task 5 : nouvelles valeurs exposées
      subscription,
      refreshSubscription,
      canWrite,
      isAuthenticated: !!user && !!token
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
